/* SIZESOFTX
=========================================================================================
* Descripción: Layout maestro (AppBar + Drawer) del panel de soporte
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 15/09/2026 | José Luis Aragón   Cervantes     | Menús por sección para accesos, invitaciones y prospección
* ---------------------------------------------------------------------------------------
*/

import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Storefront as StorefrontIcon,
  Payments as PaymentsIcon,
  MonitorHeart as MonitorIcon,
  ReceiptLong as BitacoraIcon,
  Tune as ComunicacionesIcon,
  Campaign as CampaniasIcon,
  AdminPanelSettings as UsuariosIcon,
  VpnKey as LicenciaLiberacionIcon,
  ContactMail as InvitacionesIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  SupportAgent as SoporteIcon,
  MenuBook as ManualesIcon,
  SmartToy as AgenteIcon,
} from "@mui/icons-material";
import { useSupportAuth } from "../context/SupportAuthContext";

const drawerWidth = 250;

const SectionTitle = ({ label }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      px: 2,
      pt: 2,
      pb: 0.75,
      color: "text.secondary",
      fontWeight: 800,
      letterSpacing: 0.4,
      textTransform: "uppercase",
    }}
  >
    {label}
  </Typography>
);

const NavItem = ({ to, icon, label, disabled = false }) => {
  const location = useLocation();
  const selected = !disabled && location.pathname === to;
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={disabled ? "div" : "a"}
        href={disabled ? undefined : `#${to}`}
        selected={selected}
        disabled={disabled}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />
      </ListItemButton>
    </ListItem>
  );
};

const MasterLayout = () => {
  const { user, logout, hasRole, authReady, isAuthenticated } = useSupportAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);

  if (!authReady) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const canAdmin = hasRole("SA");
  const canInvitaciones = hasRole("SA") || hasRole("SOPORTE");
  const canSoporte = hasRole("SA") || hasRole("SOPORTE");

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, background: "#0b2a4a" }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Support SizeSoftX
          </Typography>
          <Button
            component="a"
            href="#/resumen"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ mr: 2, textTransform: "none" }}
          >
            Main
          </Button>
          <Tooltip title="Menú del usuario">
            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-usuario"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2" }}>
                <PersonIcon fontSize="small" />
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            id="menu-usuario"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.nombre} ({user?.email})
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleClose();
                logout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Cerrar sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: "border-box" },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: "auto" }}>
          <List>
            <NavItem to="/resumen" icon={<DashboardIcon />} label="Resumen" />

            <SectionTitle label="Operación de soporte" />
            <NavItem to="/tenants" icon={<StorefrontIcon />} label="Licencias y Tenants" />
            <NavItem to="/estadocuenta" icon={<PaymentsIcon />} label="Estado de cuenta" />
            <NavItem to="/comunicaciones" icon={<ComunicacionesIcon />} label="Comunicaciones" />
            <NavItem to="/grafana" icon={<MonitorIcon />} label="Grafana" />
            <NavItem to="/bitacora" icon={<BitacoraIcon />} label="Bitácora" />
            <NavItem
              to="/tickets"
              icon={<SoporteIcon />}
              label={canSoporte ? "Soporte y soluciones" : "Soporte y soluciones (requiere SA/SOPORTE)"}
              disabled={!canSoporte}
            />
            {canAdmin && <NavItem to="/liberacion-licencia" icon={<LicenciaLiberacionIcon />} label="Liberar licencia" />}

            <SectionTitle label="Invitaciones y prospección" />
            <NavItem
              to="/invitaciones"
              icon={<InvitacionesIcon />}
              label={canInvitaciones ? "Invitaciones / Contrato" : "Invitaciones / Contrato (requiere SA/SOPORTE)"}
              disabled={!canInvitaciones}
            />
            <NavItem to="/campanias" icon={<CampaniasIcon />} label="Promocionales" />

            <SectionTitle label="Control de cuentas y accesos" />
            <NavItem
              to="/usuarios"
              icon={<UsuariosIcon />}
              label={canAdmin ? "Usuarios y roles" : "Usuarios y roles (requiere SA)"}
              disabled={!canAdmin}
            />
            <NavItem to="/manuales-agente" icon={<ManualesIcon />} label={canAdmin ? "Manuales de soporte" : "Manuales de soporte (requiere SA)"} disabled={!canAdmin} />
            <NavItem to="/configuracion-agente" icon={<AgenteIcon />} label={canAdmin ? "Agente: reglas y respuestas" : "Agente: reglas (requiere SA)"} disabled={!canAdmin} />
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default MasterLayout;
